import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Modal, Progress, Select, Space, TreeSelect, message } from "antd";
import moment from "moment";
import { ECharts } from "echarts";
import * as echarts from "echarts";
import { ChartHeader, ChartHeaderColorBlock, ChartInfo, ChartName, ChartUnit, ChartWrapper, ChartsWrapper, EChartsWrapper, FilterBar, FilterItem, FilterItemLabel, WaveformFileViewerWrapper } from "./WaveformFileViewer.styled";
import WaveformFileApi from "../../../../../../../api/WaveformFileApi";
import DeviceApi from "../../../../../../../api/DeviceApi";
import DeviceTemplateApi from "../../../../../../../api/DeviceTemplateApi";
import SessionService from "../../../../../../../services/session/SessionService";
import StringUtils from "../../../../../../../util/StringUtils";
import NumberUtils from "../../../../../../../util/NumberUtils";
import Api from "../../../../../../../api/Api";
import SeriesColorUtils from "../../../../../../../util/SeriesColorUtils";

interface WaveformFileViewerProps {
    waveformFileView: any,
    onClose: () => void,
}

let WaveformFileViewer: React.FC<WaveformFileViewerProps> = (props) => {
    // params
    let waveformFileView = props.waveformFileView;

    // api
    let waveformFileApi = new WaveformFileApi();
    let deviceApi = new DeviceApi();
    let deviceTemplateApi = new DeviceTemplateApi();
    let [messageApi, contextHolder] = message.useMessage();

    // state
    let [loading, setLoading] = useState(true);
    let [downloadProgressOpen, setDownloadProgressOpen] = useState(false);
    let [downloadProgressPercent, setDownloadProgressPercent] = useState(0);
    let [currentPageText, setCurrentPageText] = useState('');
    let [charts, setCharts] = useState<any[]>([]);

    // ref
    let echartsInstancesRef = useRef<any[]>([]);
    let deviceTemplateViewRef = useRef<any>(null);

    let onPrevPageRef = useRef<() => void>(null);
    let onNextPageRef = useRef<() => void>(null);
    let currentPageRef = useRef<any>(null);
    let pagesRef = useRef<any[]>(null);

    // functions
    let getWaveformFileMetas = (product) => {
        if (product.model == 'QSYJC-EWMS-A-02') {
            return {
                sampleSize: 16,
                sampleChannels: 4,
            }
        } else {
            throw 'Unknown sample metas for product model: ' + product.model;
        }
    };

    let formatTimestampUs = (timestampUs) => {
        if (timestampUs == null) {
            return '';
        }
        return moment(parseInt(timestampUs) / 1000).format('YYYY-MM-DD HH:mm:ss');
    };

    let timestampToTime = (timestampUs) => {
        var timestamp = timestampUs / 1000;
        var date = new Date(timestamp);
        var date_str = moment(date).format('YYYY-MM-DD HH:mm:ss.SSS');
        var us = (timestampUs % 1000);
        var us_str = '' + us;
        for (var i = us_str.length; i < 3; i++) {
            us_str = '0' + us_str;
        }

        var time = `${date_str}${us_str}`;
        return time;
    }

    let timestampToTimeAxis = (timestampUs) => {
        var timestamp = timestampUs / 1000;
        var date = new Date(timestamp);
        var date_str = moment(date).format('HH:mm:ss.SSS');
        var us = (timestampUs % 1000);
        var us_str = '' + us;
        for (var i = us_str.length; i < 3; i++) {
            us_str = '0' + us_str;
        }

        var time = `${date_str}${us_str}`;
        return time;
    }

    let parseData = (dataView, page) => {
        let deviceTemplateView = deviceTemplateViewRef.current;
        let deviceTemplate = deviceTemplateView.deviceTemplate;
        let product = deviceTemplateView.product;

        let configurationJson = deviceTemplate.configuration;
        let configuration = JSON.parse(configurationJson ? configurationJson : '{}');
        let properties = configuration.properties;

        let settings = JSON.parse(product.settings);
        let settingsGroups = settings?.settingsGroups ?? [];
        let settingsGroup = settingsGroups.find((e) => e.type == 'STANDARD_ADC_WAVEFORM');
        let channels = settingsGroup.channels;

        let initSeriesArr = [];
        let seriesArr = [];
        for (let i = 0; i < channels; i++) {
            let property = properties.find(property => property.registerAddress == `ch${i + 1}`)
            if (property == null) {
                continue;
            }

            let series: any = {};
            series.name = property.name;
            series.type = 'line';
            series.color = SeriesColorUtils.getSeriesColor(seriesArr.length);
            series.smooth = false;
            series.showSymbol = false;
            series.data = [];
            series.property = property;
            seriesArr.push(series);

            let initSeries: any = {};
            initSeries.name = property.name;
            initSeries.type = 'line';
            initSeries.color = SeriesColorUtils.getSeriesColor(initSeriesArr.length);
            initSeries.smooth = false;
            initSeries.showSymbol = false;
            initSeries.data = [];
            initSeries.property = property;
            initSeriesArr.push(initSeries);
        }

        let sampleMetas = getWaveformFileMetas(product);
        let sampleSize = sampleMetas.sampleSize;
        let sampleChannels = sampleMetas.sampleChannels;
        let i = page.pageStart;
        while (i + sampleSize <= page.pageEnd) {
            // timestamp(us)
            let timeUs = Number(dataView.getBigUint64(i, true));
            i += 8;

            // channel values
            for (let j = 0; j < sampleChannels; j++, i+=2) {
                let property = properties.find(property => property.registerAddress == `ch${j + 1}`)
                if (property != null) {
                    let chVal = dataView.getUint16(i, true);
                    let metricVal = chVal;

                    // linear mapping
                    let linearMapping = property.linearMapping;
                    if (linearMapping != null
                        && !StringUtils.isEmpty(linearMapping.x1)
                        && !StringUtils.isEmpty(linearMapping.y1)
                        && !StringUtils.isEmpty(linearMapping.x2)
                        && !StringUtils.isEmpty(linearMapping.y2)
                    ) {
                        let x1 = parseFloat(linearMapping.x1);
                        let y1 = parseFloat(linearMapping.y1);
                        let x2 = parseFloat(linearMapping.x2);
                        let y2 = parseFloat(linearMapping.y2);
                        if (!NumberUtils.equals(x1, x2)) {
                            let k = (y2 - y1) / (x2 - x1);
                            let b = y1 - k * x1;
                            metricVal = k * chVal + b;
                        }
                    }

                    // push to series data
                    seriesArr[j].data.push([timeUs, metricVal]);
                }
            }
        }
        seriesArr = seriesArr.filter((e) => {
            return e.data.length > 0;
        });

        let charts = [];
        for (let i = 0; i < seriesArr.length; i++) {
            charts.push({
                initSeries: initSeriesArr[i],
                series: seriesArr[i],
            });
        }
        setCharts(charts);
    };

    let initChart = (dom: HTMLElement, seriesIndex) => {
        if (dom == null) {
            let echartsInstance = echartsInstancesRef.current[seriesIndex];
            echartsInstance?.dispose();
            echartsInstancesRef.current[seriesIndex] = null;
            return;
        }
        
        let chart = charts[seriesIndex];
        let series = chart.series;
        let initSeries = chart.initSeries;
        let property = series.property;

        let minTime = series.data[0][0];
        let maxTime = series.data[series.data.length - 1][0];
        let count = series.data.length;

        let echartsDom = dom;
        if (echarts.getInstanceByDom(echartsDom)) {
            echarts.getInstanceByDom(echartsDom).dispose();
        }
        let echartsInstance = echarts.init(echartsDom);
        echartsInstancesRef.current[seriesIndex] = echartsInstance;

        // selected series
        let selected = [];

        // set echarts options
        let option = {
            animation: false,
            grid: {
                x: 50,
                y: 40,
                x2: 20,
                y2: 40,
            },
            toolbox: {
                feature: {
                    saveAsImage: {
                        name: waveformFileView.waveformFile.name + '-' + property.name,
                        title: '',
                        pixelRatio: 2,
                        icon: 'image://data:image/png;base64,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'
                    }
                }
            },
            tooltip: {
                trigger: 'axis',
                backgroundColor: "rgba(0,0,0,0.7)",
                formatter: function (params) {
                    if (params == null) {
                        return '';
                    }
                    let seriesContent = '';
                    for (let param of params) {
                        if (param.value == null) {
                            continue;
                        }
                        seriesContent += `
                            <div style="color: #EEEEEE;">
                                <div style="margin: 0px 0px 5px 0px;">
                                    <div style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 5px; background-color: ${param.color}"></div>
                                    <div style="display: inline-block">${param.seriesName}</div>
                                </div>
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 50px;">测量值:</span> 
                                    <span style="display: inline-block">${param.value[1].toFixed(2)}</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">时间:</span> 
                                    <span style="display: inline-block">${timestampToTime(param.value[0])}</span>
                                </div>
                            </div>
                        `;
                    }
                    return seriesContent;
                },
                axisPointer: {
                    animation: false
                }
            },
            xAxis: {
                minorTick: {
                    show: true
                },
                splitNumber: 2,
                axisLabel: {
                    show: true,
                    formatter: function (value, index) {
                        return timestampToTimeAxis(Number(value));
                    }
                },
                minorSplitLine: {
                    show: true
                },
                min: minTime,
                max: maxTime,
            },
            yAxis: {
                name: property.name,
                nameTextStyle: {
                    padding: [0, 0, 0, 50],
                },
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                min: property.min != null ? parseFloat(property.min) : 'dataMin',
                max: property.max != null ? parseFloat(property.max) : 'dataMax',
            },
            dataZoom: [
                {
                    show: true,
                    type: 'inside',
                    filterMode: 'none',
                    xAxisIndex: [0],
                    bottom: -20, 
                },
                {
                    show: true,
                    type: 'slider',
                    filterMode: 'none',
                    xAxisIndex: [0],
                    bottom: -20, 
                    start: 0,
                    end: 100,
                },
            ],
            series: initSeries,
        };
        echartsInstance.setOption(option);
        echartsInstance.group = 'WaveformFileMetrics';
        echarts.connect('WaveformFileMetrics');

        let onDataZoom = (e) => {
            if (e.batch) {
                e = e.batch[0];
            }
            let startPercent = e.start;
            let endPercent = e.end;

            let startTime = minTime + (maxTime - minTime) * startPercent / 100.0;
            let endTime = minTime + (maxTime - minTime) * endPercent / 100.0;
            let startIndex = 0;
            let endIndex = count;

            for (let i = 0; i < series.data.length; i++) {
                let dp = series.data[i];
                if (dp[0] > startTime) {
                    startIndex = i;
                    break;
                }
            }
            for (let i = 0; i < series.data.length; i++) {
                let dp = series.data[i];
                if (dp[0] > endTime) {
                    endIndex = i;
                    break;
                }
            }

            let range = endIndex - startIndex;
            let rangeLimit = 2048;
            if (range > rangeLimit) {
                // sampling
                let options = { series: [] };
                {
                    let downSamplingData = [];
                    let data = series.data.slice(startIndex, endIndex);
                    let frameSize1 = Math.floor(range / rangeLimit);
                    let frameSize2 = Math.max(Math.floor(frameSize1 / 50), 1);
                    let frameSize = frameSize1;
                    for (let i = 0; i < data.length; i += frameSize) {
                        let frameMinValue = Number.MAX_SAFE_INTEGER;
                        let frameMinIndex = 0;
                        let frameMaxValue = Number.MIN_SAFE_INTEGER;
                        let frameMaxIndex = 0;
                        for (let j = 0; j < frameSize && i + j < data.length; j++) {
                            let dp = data[i + j];
                            if (dp[1] < frameMinValue) {
                                frameMinValue = dp[1];
                                frameMinIndex = j;
                            }
                            if (dp[1] > frameMaxValue) {
                                frameMaxValue = dp[1];
                                frameMaxIndex = j;
                            }
                        }
                        let minIndex = Math.min(frameMinIndex, frameMaxIndex);
                        let maxIndex = Math.max(frameMinIndex, frameMaxIndex);
                        downSamplingData.push(data[i + minIndex]);
                        if (minIndex != maxIndex) {
                            downSamplingData.push(data[i + maxIndex]);
                        }
                    }
                    options.series.push({ data: downSamplingData });
                }
                echartsInstance.setOption(options);
            } else {
                let options = { series: [] };
                {
                    let data = series.data.slice(startIndex, endIndex);
                    options.series.push({ data });
                }
                echartsInstance.setOption(options);
            }
        };
        echartsInstance.on('datazoom', onDataZoom);
        onDataZoom({ start: 0, end: 100 });

        echartsInstance.on('legendselectchanged', function (event: any) {
            selected = event.selected;
        });
        echartsInstance.on('legendselectall', function (event: any) {
            selected = event.selected;
        });
        echartsInstance.on('legendinverseselect', function (event: any) {
            selected = event.selected;
        });
    };


    // effect
    useEffect(() => {
        let onWindowResize = () => {
            for(let echartsInstance of echartsInstancesRef.current) {
                if (echartsInstance) {
                    echartsInstance.resize();
                }
            }
        };
        window.addEventListener('resize', onWindowResize);
        return () => {
            window.removeEventListener('resize', onWindowResize);
        };
    });

    useEffect(() => {
        let download = async () => {
            // get device template
            let deviceTemplateView = (await deviceTemplateApi.getDeviceTemplateViewById(waveformFileView.device.deviceTemplateId)).data.data;
            deviceTemplateViewRef.current = deviceTemplateView;

            // download file
            let dataFileLength = waveformFileView.waveformFile.size;
            let dataFileUrl = Api.URL_PREFIX + `/waveform-file/download?id=${waveformFileView.waveformFile.id}`
            let response = await fetch(dataFileUrl, {
                cache: 'no-store',
                headers: {
                    'Authorization': 'Bearer ' + SessionService.getInstance().getAuthoriazation(),
                }
            });
            let reader = response.body.getReader();
            let chunks = [];
            let received = 0;
            setDownloadProgressOpen(true);
            setDownloadProgressPercent(0);
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                chunks.push(value);
                received += value.length;
                let progressPercent = Math.floor(received / dataFileLength * 100);
                setDownloadProgressPercent(progressPercent);
            }
            setTimeout(()=> {
                setDownloadProgressOpen(false);
            }, 1000);

            let chunksAll = new Uint8Array(received);
            let position = 0;
            for (let chunk of chunks) {
                chunksAll.set(chunk, position);
                position += chunk.length;
            }

            let pageSize = 5 * 1024 * 1024; // 5MB per page
            let sampleSize = getWaveformFileMetas(deviceTemplateView.product).sampleSize;
            let dataView = new DataView(chunksAll.buffer);
            let byteLength = dataView.byteLength;
            let pageLength = Math.round(pageSize / sampleSize) * sampleSize;

            let pages = [];
            for (let i = 0; i < byteLength; i += pageLength) {
                let pageStart = i;
                let pageEnd = Math.min(i + pageLength, byteLength);
                pages.push({
                    pageNo: pages.length + 1,
                    pageStart: pageStart,
                    pageEnd: pageEnd,
                });
            }
            pagesRef.current = pages;
            currentPageRef.current = pages[0];

            let formatPageInfo = (page) => {
                let toMB = (byte) => { return (byte / 1024 / 1024).toFixed(2) + 'MB'; };
                return `${page.pageNo}/${pages.length} 页 ${toMB(page.pageStart)}-${toMB(page.pageEnd)} / ${toMB(byteLength)}`;
            };

            let currentPageIndex = 0;
            onPrevPageRef.current = () => {
                currentPageIndex = (currentPageIndex - 1) % pages.length;
                if (currentPageIndex < 0) {
                    currentPageIndex += pages.length;
                }
                let currentPage = pages[currentPageIndex];
                parseData(dataView, currentPage)
                currentPageRef.current = currentPage;
                setCurrentPageText(formatPageInfo(currentPage));
            };

            onNextPageRef.current = () => {
                currentPageIndex = (currentPageIndex + 1) % pages.length;
                if (currentPageIndex < 0) {
                    currentPageIndex += pages.length;
                }
                let currentPage = pages[currentPageIndex];
                parseData(dataView, currentPage)
                currentPageRef.current = currentPage;
                setCurrentPageText(formatPageInfo(currentPage));
            };

            setCurrentPageText(formatPageInfo(currentPageRef.current));
            parseData(dataView, currentPageRef.current);
        }
        download();
    }, [waveformFileView]);

    // render
    return <WaveformFileViewerWrapper>
        <FilterBar>
            <Space size="middle">
                <FilterItem>
                    <FilterItemLabel>&nbsp;&nbsp;&nbsp;&nbsp;</FilterItemLabel>
                    <Button size="small" type="primary" onClick={() => onPrevPageRef.current ? onPrevPageRef.current() : null}>上一页</Button>
                </FilterItem>
                <FilterItem>
                    <FilterItemLabel>&nbsp;&nbsp;&nbsp;&nbsp;</FilterItemLabel>
                    <Button size="small" type="primary" onClick={() => onNextPageRef.current ? onNextPageRef.current() : null}>下一页</Button>
                </FilterItem>
                <FilterItem>
                    <FilterItemLabel>&nbsp;&nbsp;&nbsp;&nbsp;</FilterItemLabel>
                    <span>{currentPageText}</span>
                </FilterItem>
                <FilterItem>
                    <FilterItemLabel>&nbsp;&nbsp;&nbsp;&nbsp;</FilterItemLabel>
                    <span>{waveformFileView.waveformFile.name}</span>
                </FilterItem>
            </Space>
            <Space size="middle">
                <Button size="small" type="primary" danger onClick={()=> props.onClose()}>关闭</Button>
                <Modal
                    title="数据下载进度"
                    closable={false}
                    footer={null}
                    open={downloadProgressOpen} >
                    <Progress percent={downloadProgressPercent} size="small" />
                </Modal>
            </Space>
        </FilterBar>
        <ChartsWrapper>
        { 
            charts.map((e, index)=> {
                let property = e.series.property;
                return <ChartWrapper key={index}>
                    <ChartHeader>
                        <ChartName>{property.name}</ChartName>
                        <ChartHeaderColorBlock style={{backgroundColor: SeriesColorUtils.getSeriesColor(index)}} />
                        <ChartInfo>
                            <ChartUnit>单位：{StringUtils.emptyToSlash(property.unit)}</ChartUnit>
                        </ChartInfo>
                    </ChartHeader>
                    <EChartsWrapper ref={(dom)=> initChart(dom, index)}></EChartsWrapper>
                </ChartWrapper>
            })
        }
        </ChartsWrapper>
    </WaveformFileViewerWrapper>
};

export default WaveformFileViewer;

